import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function UpgradeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [phase, setPhase] = useState("polling"); // polling | success | failed | timeout
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    document.title = "Confirming payment · RigBooks";
    if (!sessionId) {
      setPhase("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && attempts.current < 12) {
        attempts.current += 1;
        try {
          const r = await api(`/payments/status/${sessionId}`);
          setInfo(r);
          if (r.payment_status === "paid") {
            setPhase("success");
            return;
          }
          if (r.status === "expired") {
            setPhase("failed");
            return;
          }
        } catch (e) {
          console.debug("Payment status poll retry:", e?.message || e);
        }
        await new Promise((res) => setTimeout(res, 2500));
      }
      if (!cancelled) setPhase("timeout");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md rounded-3xl border border-rig-border bg-rig-surface p-8 text-center">
        {phase === "polling" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-rig-accent" />
            <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-white">Confirming payment…</h2>
            <p className="mt-2 text-sm text-rig-dim">This may take a few seconds. Don't close this tab.</p>
          </>
        )}
        {phase === "success" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-rig-accent">
              <Check className="h-10 w-10 text-rig-accentText" strokeWidth={3} />
            </div>
            <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-rig-accent">Welcome to Pro</h2>
            <p className="mt-2 text-sm text-white">
              {info ? `Paid ${(info.amount_total / 100).toFixed(2)} ${info.currency?.toUpperCase()}. ` : ""}
              You now have unlimited receipts, IFTA, bookkeeper sharing &amp; the year-end audit bundle.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              data-testid="upgrade-success-continue"
              className="mt-6 h-12 w-full bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
            >
              Start using Pro
            </Button>
          </>
        )}
        {(phase === "failed" || phase === "timeout") && (
          <>
            <AlertCircle className="mx-auto h-14 w-14 text-rig-warning" />
            <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-white">
              {phase === "timeout" ? "Still processing" : "Payment not completed"}
            </h2>
            <p className="mt-2 text-sm text-rig-dim">
              {phase === "timeout"
                ? "Stripe is taking a bit. We'll keep checking — come back to Pro from your Profile shortly."
                : "We didn't see a completed payment. You can try again."}
            </p>
            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="h-12 flex-1 border-rig-borderStrong bg-transparent text-white hover:bg-rig-surface2"
              >
                Back to Dashboard
              </Button>
              <Button
                onClick={() => navigate("/upgrade")}
                className="h-12 flex-1 bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
              >
                Try again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
